import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import PropertyContextHeader from '../../components/ui/PropertyContextHeader';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import OfferCard from './components/OfferCard';
import SortingToolbar from './components/SortingToolbar';
import AddOfferModal from './components/AddOfferModal';
import SubscriptionLimitModal from './components/SubscriptionLimitModal';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import EmptyState from './components/EmptyState';
import { useAuth } from '../../contexts/AuthContext';
import { propertyService } from '../../services/propertyService';
import { offerService } from '../../services/offerService';

const OfferManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();

  const subscriptionTier = userProfile?.subscription_tier || 'free';

  const [property, setProperty] = useState(null);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('custom');
  const [viewMode, setViewMode] = useState('grid');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isLimitModalOpen, setIsLimitModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [deletingOffer, setDeletingOffer] = useState(null);
  const [draggedOfferId, setDraggedOfferId] = useState(null);

  // Get propertyId from URL query params
  const searchParams = new URLSearchParams(location.search);
  const propertyId = searchParams?.get('propertyId');

  // Load property and offers
  useEffect(() => {
    if (propertyId) {
      loadPropertyAndOffers();
    }
  }, [propertyId]);

  const loadPropertyAndOffers = async () => {
    setLoading(true);
    setError(null);

    // Load property
    const { data: propertyData, error: propertyError } = await propertyService?.getProperty(propertyId);
    
    if (propertyError) {
      setError(propertyError);
      setLoading(false);
      return;
    }

    // Transform property data
    const transformedProperty = {
      id: propertyData?.id,
      address: propertyData?.address,
      owner: propertyData?.property_owners?.map(o => o?.name)?.join(' & ') || 'No owner',
      listingPrice: propertyData?.listing_price,
      status: propertyData?.status,
      image: propertyData?.image_url,
      imageAlt: propertyData?.image_alt || 'Property image'
    };

    setProperty(transformedProperty);

    // Load offers
    const { data: offersData, error: offersError } = await offerService?.getOffers(propertyId);
    
    if (offersError) {
      setError(offersError);
    } else {
      setOffers(offersData || []);
      setProperty(prev => ({ ...prev, offerCount: offersData?.length || 0 }));
    }

    setLoading(false);
  };

  // Breadcrumb configuration
  const breadcrumbItems = [
  { label: 'Dashboard', path: '/agent-dashboard', icon: 'Home' },
  { label: 'Properties', path: '/agent-dashboard', icon: 'Building2' },
  { label: property?.address, path: null }];


  // Handle add offer
  const handleAddOffer = async () => {
    console.log('[OfferManagement] ========== HANDLE ADD OFFER ==========');
    console.log('[OfferManagement] Timestamp:', new Date()?.toISOString());
    console.log('[OfferManagement] Subscription tier:', subscriptionTier);
    console.log('[OfferManagement] Current offers count:', offers?.length);

    // Check subscription limit for free users
    if (subscriptionTier === 'free') {
      const limitCheck = await offerService?.checkOfferLimit(propertyId, userProfile?.id);
      
      console.log('[OfferManagement] Limit check result:', limitCheck);
      
      if (!limitCheck?.allowed) {
        console.log('[OfferManagement] ❌ Limit reached - showing upgrade modal');
        setIsLimitModalOpen(true);
        return;
      }
      
      console.log('[OfferManagement] ✅ Within limit - proceeding to add offer');
    }

    setIsAddModalOpen(true);
  };

  // Property context actions
  const propertyActions = [
  {
    label: 'Add New Offer',
    icon: 'Plus',
    variant: 'default',
    onClick: handleAddOffer
  },
  {
    label: 'Compare Offers',
    icon: 'FileText',
    variant: 'default',
    onClick: () => navigate('/report-generation', { state: { propertyId } }),
    disabled: offers?.length === 0
  }];


  // Sorting logic
  const getSortedOffers = () => {
    const sorted = [...offers];

    switch (sortBy) {
      case 'price-high':
        return sorted?.sort((a, b) => b?.offerPrice - a?.offerPrice);
      case 'price-low':
        return sorted?.sort((a, b) => a?.offerPrice - b?.offerPrice);
      case 'closing-early':
        return sorted?.sort((a, b) => new Date(a.closingDate) - new Date(b.closingDate));
      case 'closing-late':
        return sorted?.sort((a, b) => new Date(b.closingDate) - new Date(a.closingDate));
      case 'submitted-new':
        return sorted?.sort((a, b) => new Date(b.submittedDate) - new Date(a.submittedDate));
      case 'submitted-old':
        return sorted?.sort((a, b) => new Date(a.submittedDate) - new Date(b.submittedDate));
      case 'custom':
      default:
        return sorted;
    }
  };

  const sortedOffers = getSortedOffers();

  // Handle edit offer
  const handleEditOffer = (offerId) => {
    const offer = offers?.find((o) => o?.id === offerId);
    setEditingOffer(offer);
    setIsAddModalOpen(true);
  };

  // Handle delete offer
  const handleDeleteOffer = (offerId) => {
    const offer = offers?.find((o) => o?.id === offerId);
    setDeletingOffer(offer);
    setIsDeleteModalOpen(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (deletingOffer) {
      const { error } = await offerService?.deleteOffer(deletingOffer?.id);
      
      if (error) {
        console.error('Delete error:', error);
        return;
      }

      setOffers((prev) => prev?.filter((o) => o?.id !== deletingOffer?.id));
      setProperty(prev => ({ ...prev, offerCount: (prev?.offerCount || 1) - 1 }));
      setIsDeleteModalOpen(false);
      setDeletingOffer(null);
    }
  };

  // Handle offer submission
  const handleOfferSubmit = async (offerData) => {
    console.log('[OfferManagement] ========== HANDLE OFFER SUBMIT ==========');
    console.log('[OfferManagement] Timestamp:', new Date()?.toISOString());
    console.log('[OfferManagement] Is editing existing offer:', !!editingOffer);
    console.log('[OfferManagement] Offer data received:', {
      buyerName: offerData?.buyerName,
      offerPrice: offerData?.offerPrice,
      downPayment: offerData?.downPayment,
      financingType: offerData?.financingType,
      earnestMoney: offerData?.earnestMoney,
      closingDate: offerData?.closingDate,
      hasPdfFile: !!offerData?.pdfFile
    });
    
    if (editingOffer) {
      console.log('[OfferManagement] Updating existing offer ID:', editingOffer?.id);
      // Update existing offer
      const { data, error } = await offerService?.updateOffer(editingOffer?.id, offerData);

      if (error) {
        console.error('[OfferManagement] ❌ Update error:', error);
        console.error('[OfferManagement] Error details:', {
          message: error?.message,
          code: error?.code,
          details: error?.details
        });
        return;
      }

      console.log('[OfferManagement] ✅ Offer updated successfully');
      setOffers((prev) => prev?.map((o) =>
        o?.id === editingOffer?.id ? { ...o, ...offerData } : o
      ));
      
      // Close modal after successful update
      console.log('[OfferManagement] Closing modal after update');
      setIsAddModalOpen(false);
      setEditingOffer(null);
    } else {
      console.log('[OfferManagement] Creating new offer for property ID:', propertyId);
      // Create new offer
      const { data, error } = await offerService?.createOffer(propertyId, offerData);

      if (error) {
        console.error('[OfferManagement] ❌ Create error:', error);
        console.error('[OfferManagement] Error details:', {
          message: error?.message,
          code: error?.code,
          details: error?.details
        });
        return;
      }

      console.log('[OfferManagement] ✅ Offer created successfully, ID:', data?.id);

      // If there's a PDF file, upload it and update the offer
      if (offerData?.pdfFile && data?.id) {
        console.log('[OfferManagement] Uploading PDF file:', offerData?.pdfFile?.name);
        const { data: uploadData, error: uploadError } = await offerService?.uploadContractPdf(
          propertyId,
          data?.id,
          offerData?.pdfFile
        );

        if (!uploadError && uploadData?.path) {
          console.log('[OfferManagement] ✅ PDF uploaded successfully, path:', uploadData?.path);
          // Update the offer with ONLY the PDF path
          const { error: updateError } = await offerService?.updateOffer(data?.id, {
            contractPdfUrl: uploadData?.path
          });

          if (updateError) {
            console.error('[OfferManagement] ❌ Failed to update offer with PDF path:', updateError);
          } else {
            console.log('[OfferManagement] ✅ Offer updated with PDF path');
            // Update local state with PDF URL
            data.contract_pdf_url = uploadData?.path;
          }
        } else {
          console.error('[OfferManagement] ❌ PDF upload error:', uploadError);
        }
      } else {
        console.log('[OfferManagement] No PDF file to upload');
      }

      // Transform snake_case to camelCase for local state
      const newOffer = {
        id: data?.id,
        buyerName: data?.buyer_name,
        offerPrice: data?.offer_price,
        downPayment: data?.down_payment,
        financingType: data?.financing_type,
        earnestMoney: data?.earnest_money,
        closingDate: data?.closing_date,
        contingencies: data?.contingencies || [],
        appraisalContingency: data?.appraisal_contingency,
        inspectionPeriod: data?.inspection_period,
        sellerConcessions: data?.seller_concessions,
        buyerAgent: data?.buyer_agent,
        preApprovalAmount: data?.pre_approval_amount,
        escalationClause: data?.escalation_clause,
        submittedDate: data?.submitted_date,
        expirationDate: data?.expiration_date,
        internalNotes: data?.internal_notes,
        contractPdfUrl: data?.contract_pdf_url
      };

      console.log('[OfferManagement] Adding new offer to local state');
      setOffers((prev) => [...prev, newOffer]);
      setProperty(prev => ({ ...prev, offerCount: (prev?.offerCount || 0) + 1 }));
    }

    console.log('[OfferManagement] Closing modal after successful submission');
    setIsAddModalOpen(false);
    setEditingOffer(null);
    console.log('[OfferManagement] ========== HANDLE OFFER SUBMIT COMPLETE ==========');
  };

  // Handle notes update
  const handleNotesUpdate = async (offerId, notes) => {
    const { error } = await offerService?.updateOfferNotes(offerId, notes);
    
    if (error) {
      console.error('Update notes error:', error);
      return;
    }

    setOffers((prev) => prev?.map((o) =>
      o?.id === offerId ? { ...o, internalNotes: notes } : o
    ));
  };

  // Drag and drop handlers
  const handleDragStart = (offerId) => {
    setDraggedOfferId(offerId);
  };

  const handleDragOver = (e, targetOfferId) => {
    e?.preventDefault();
    if (draggedOfferId && draggedOfferId !== targetOfferId && sortBy === 'custom') {
      const draggedIndex = offers?.findIndex((o) => o?.id === draggedOfferId);
      const targetIndex = offers?.findIndex((o) => o?.id === targetOfferId);

      const newOffers = [...offers];
      const [draggedOffer] = newOffers?.splice(draggedIndex, 1);
      newOffers?.splice(targetIndex, 0, draggedOffer);

      setOffers(newOffers);
    }
  };

  const handleDragEnd = async () => {
    if (draggedOfferId && sortBy === 'custom') {
      // Save new order to database
      const offerIds = offers?.map(o => o?.id);
      await offerService?.reorderOffers(propertyId, offerIds);
    }
    setDraggedOfferId(null);
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="text-primary animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading offers...</p>
          </div>
        </div>
      </>
    );
  }

  if (error || !property) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center max-w-md">
            <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">Failed to Load Property</h2>
            <p className="text-muted-foreground mb-4">{error || 'Property not found'}</p>
            <Button onClick={() => navigate('/agent-dashboard')}>Back to Dashboard</Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Breadcrumbs items={breadcrumbItems} />
      <PropertyContextHeader property={property} actions={propertyActions} />
      <main className="max-w-[1440px] mx-auto px-4 lg:px-8 py-6 md:py-8">
        {/* Page Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                Offer Management
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Add, organize, and compare offers for this property
              </p>
            </div>
          </div>
        </div>

        {/* Sorting Toolbar */}
        {offers?.length > 0 && (
        <div className="mb-6">
            <SortingToolbar
            sortBy={sortBy}
            onSortChange={setSortBy}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            totalOffers={offers?.length} />
          </div>
        )}

        {/* Quick Actions - Top */}
        {offers?.length > 0 && (
        <div className="mb-6 bg-card rounded-lg border border-border p-4 md:p-6 shadow-elevation-sm">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Icon name="Lightbulb" size={20} className="text-accent" />
                <p className="text-sm md:text-base text-muted-foreground">
                  Ready to present these offers to your seller?
                </p>
              </div>
              <Button
              variant="default"
              size="lg"
              onClick={() => navigate('/report-generation', { state: { propertyId } })}
              iconName="FileText"
              iconPosition="left">

                Compare Offers
              </Button>
            </div>
          </div>
        )}

        {/* Offers Grid/List */}
        {offers?.length === 0 ? (
        <EmptyState onAddOffer={handleAddOffer} />
        ) : (
        <div className={`grid gap-4 md:gap-6 ${
        viewMode === 'grid' ? 'grid-cols-1 lg:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1'}`
        }>
            {sortedOffers?.map((offer) => (
          <div
            key={offer?.id}
            draggable={sortBy === 'custom'}
            onDragStart={() => handleDragStart(offer?.id)}
            onDragOver={(e) => handleDragOver(e, offer?.id)}
            onDragEnd={handleDragEnd}>
                <OfferCard
              offer={offer}
              onEdit={handleEditOffer}
              onDelete={handleDeleteOffer}
              onNotesUpdate={handleNotesUpdate}
              isDragging={draggedOfferId === offer?.id}
              dragHandleProps={{
                draggable: sortBy === 'custom'
              }}
              canDelete={offers?.length > 1} />
              </div>
          ))}
          </div>
        )}

        {/* Quick Actions Footer */}
        {offers?.length > 0 && (
        <div className="mt-8 bg-card rounded-lg border border-border p-4 md:p-6 shadow-elevation-sm">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Icon name="Lightbulb" size={20} className="text-accent" />
                <p className="text-sm md:text-base text-muted-foreground">
                  Ready to present these offers to your seller?
                </p>
              </div>
              <Button
              variant="default"
              size="lg"
              onClick={() => navigate('/report-generation', { state: { propertyId } })}
              iconName="FileText"
              iconPosition="left">

                Compare Offers
              </Button>
            </div>
          </div>
        )}
      </main>
      {/* Modals */}
      <AddOfferModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditingOffer(null);
        }}
        onSubmit={handleOfferSubmit}
        existingOffer={editingOffer} />

      <SubscriptionLimitModal
        isOpen={isLimitModalOpen}
        onClose={() => setIsLimitModalOpen(false)}
        currentPlan={subscriptionTier} />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeletingOffer(null);
        }}
        onConfirm={confirmDelete}
        offerDetails={deletingOffer} />

    </div>);

};

export default OfferManagement;